import type { ApiResponse } from '@atlas/contracts';
import { getAuthHeaders, clearSession } from './auth';

/**
 * Typed API client. Unwraps the Atlas envelope ({status,data,error,traceId})
 * and throws ApiError on failure. Auth headers are resolved per-request by
 * lib/auth: a session bearer token (email login), a Microsoft Entra access
 * token (SSO mode), or the X-Atlas-User dev persona header (dev mode).
 */

// Persona/active-user helpers live in lib/auth now; re-exported here so existing
// callers (AppShell, morning-brief) keep importing them from lib/api.
export { getActiveUser, setActiveUser } from './auth';

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      // Only claim a JSON body when one is sent — a bodyless request with a
      // JSON content-type trips Fastify's empty-JSON-body guard (→ 500),
      // which broke every DELETE issued through this client.
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(await getAuthHeaders()),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let payload: ApiResponse<T>;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(`Request failed (${res.status})`, 'network', res.status);
  }
  if (payload.status === 'error') {
    // A stale/expired session token can't recover — drop it so the next load
    // falls back to the login screen rather than looping on 401s. Exclude the
    // auth endpoints: a 401 from /auth/* is part of the login handshake (e.g. a
    // single-use link replayed by React StrictMode) and must NOT wipe a session
    // we just established.
    if (res.status === 401 && !path.startsWith('/v2/auth/')) clearSession();
    throw new ApiError(payload.error.message, payload.error.code, res.status, payload.error.details);
  }
  return payload.data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  // POST/PUT default to an empty-object body: a bodyless action POST with a
  // JSON content-type otherwise trips Fastify's empty-JSON-body guard (→ 500).
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

export function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
